<script lang="ts" setup>
  import { useQuery } from '@tanstack/vue-query'

  import { REGIONS } from '~/constants/REGIONS'
  import { useRegionsService } from '~/core/services'
  import { BngContentFit } from '~/layouts/components'

  type Props = { regionId: string }
  const props = defineProps<Props>()
  const { t } = useI18n()
  const { getRegions } = useRegionsService()

  const displayedRegions = computed(() => REGIONS
    .map((mr) => ({ ...mr, ...regions.value?.data?.find((r) => mr.code === r.code) }))
    .sort((a, b) => (b.golfs_count ?? 0) - (a.golfs_count ?? 0))
    .filter((r) => r.id !== parseInt(props.regionId))
    .filter((_, k) => k < 4))

  const { data: regions, isFetching: isFetchingRegions } = useQuery({
    queryKey: ['regions'],
    queryFn: () => getRegions(),
  })

</script>
<template>
  <div class="c-more-regions">
    <BngContentFit row>
      <div class="regions-content">
        <div class="regions-title">
          <SvgIcon
            :height="20"
            :width="20"
            :desktop-height="28"
            :desktop-width="28"
            icon="more"
          />
          <p class="title">
            <span>{{ t("golf.show-more-regions") }}</span>
          </p>
        </div>
        <template v-if="!isFetchingRegions">
          <div class="regions-cards">
            <Paper
              v-for="region in displayedRegions"
              :key="region.slug"
            >
              <RegionCard :region="region" />
            </Paper>
          </div>
          <div class="scroll-container">
            <ScrollH class="more-regions-cards">
              <Paper
                v-for="region in displayedRegions"
                :key="region.slug"
                class="hover-shadow"
              >
                <RegionCard :region="region" />
              </Paper>
            </ScrollH>
          </div>
        </template>
        <p v-else>
          Chargement des régions
        </p>
      </div>
    </BngContentFit>
  </div>
</template>
<style scoped>
.c-more-regions {
  display: flex;
  width: 100%;
  flex-direction: row;
  margin: 3em auto;
  background-color: var(--color-secondary-light);

  --displayed-more-regions: 1;

  .regions-content {
    display: flex;
    overflow: hidden;
    flex: 1 1 auto;
    flex-direction: column;
    padding: 2.5em 0;
    margin: 0 auto;

    .regions-title {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 1em;

      .c-icon {
        margin-right: 0.75em;
      }

      .title {
        span {
          color: var(--color-secondary);
          font-family: Bariol, sans-serif;
          font-size: 2em;
          font-weight: 700;
        }
      }
    }

    .regions-cards {
      display: none;
      flex-direction: row;

      .c-paper {
        flex: 1 1 25%;
        margin: 1.5em 1.5em 1.5em 0;

        &:last-child {
          margin: 1.5em 0;
        }
      }
    }

    .scroll-container {
      position: relative;
      display: flex;

      .more-regions-cards {
        position: relative;
        z-index: 0;
        display: flex;
        max-width: calc(100vw - 7em);
        box-sizing: content-box;
        flex-direction: row;
        padding: 1.5em;
        background-color: var(--color-secondary-light);
        grid-gap: 1.5em;

        .c-paper {
          overflow: hidden;
          width: 200px;
          flex: 0 0 calc((100% / var(--displayed-more-regions)) - var(--gap));
          margin: 0;

          --gap: 1.5em * (var(--displayed-more-regions) - 1) / var(--displayed-more-regions);
        }
      }

      &::after,
      &::before {
        position: absolute;
        z-index: 1;
        bottom: 0;
        width: 1.5em;
        height: 100%;
        content: "";
      }

      &::after {
        right: 0;
        background: linear-gradient(to left, var(--color-secondary-light), transparent);
      }

      &::before {
        left: 0;
        background: linear-gradient(to right, var(--color-secondary-light), transparent);
      }
    }
  }

  @media screen and (width >= 480px) {
    --displayed-more-regions: 2;
  }

  @media screen and (width >= 800px) {
    --displayed-more-regions: 3;
  }

  @media screen and (width >= 1024px) {
    .regions-content {
      .regions-cards {
        display: flex;
      }

      .scroll-container {
        display: none;
      }
    }
  }
}
</style>
